import { ThemeProvider } from "styled-components";
import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { MotionConfig } from 'framer-motion';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';

import { darkTheme, lightTheme } from './utils/Themes.js';
import Navbar from "./components/Navbar";
import HeroSection from "./components/HeroSection";
import About from "./components/About";
import Skills from "./components/Skills";
import Projects from "./components/Projects";
import Experience from "./components/Experience";
import Contact from "./components/Contact";
import Education from "./components/Education";
import Research from "./components/Research";
import Footer from "./components/Footer";
import ProjectDetails from "./components/ProjectDetails";

import styled from "styled-components";
import { BrowserRouter, Routes, Route, useLocation, Link } from 'react-router-dom';
import { getBasePath } from './blog/urls';
import Seo from './blog/Seo';
import site from './config/site.json';
const BlogListing = lazy(() => import('./blog/BlogListing'));
const BlogArticle = lazy(() => import('./blog/BlogArticle'));
function SkipLink() {
 const location=useLocation();
 return <a className="skip-link" href={getBasePath()+location.pathname+location.search+'#main'}>Skip to content</a>;
}
function RoutePosition() {
 const location = useLocation();
 useEffect(() => {
   document.getElementById('blog-static-schema')?.remove();
   const frame = requestAnimationFrame(() => {
     if (location.hash) document.getElementById(location.hash.slice(1))?.scrollIntoView();
     else { window.scrollTo({top:0,behavior:'instant'}); document.getElementById('main')?.focus({preventScroll:true}); }
   });
   return () => cancelAnimationFrame(frame);
 },[location.pathname,location.hash]);
 return null;
}


const Body = styled.div`
  background: var(--background);
  width: 100%;
  min-height: 100vh;
  color: var(--text);
`;

const Wrapper = styled.div`
  background: transparent;
  width: 100%;
  position: relative;
  z-index: 1;
  transition: all 0.3s ease-in-out;
`;

function App() {
  const [darkMode, setDarkMode] = useState(() => {
    try { return localStorage.getItem('portfolio-theme') === 'dark'; }
    catch { return false; }
  });
  const [openModal, setOpenModal] = useState({ state: false, project: null });
  const toggleDarkMode = () => setDarkMode((mode) => !mode);
  const muiTheme = useMemo(() => createTheme({ palette: { mode: darkMode ? 'dark' : 'light' } }), [darkMode]);

  useEffect(() => {
    document.documentElement.dataset.theme = darkMode ? 'dark' : 'light';
    try { localStorage.setItem('portfolio-theme', darkMode ? 'dark' : 'light'); }
    catch { /* The theme still works when browser storage is unavailable. */ }
  }, [darkMode]);

  return (
    <ThemeProvider theme={darkMode ? darkTheme : lightTheme}>
      <MuiThemeProvider theme={muiTheme}>
      <BrowserRouter basename={getBasePath() || '/'}>
      <MotionConfig reducedMotion="user">
        <RoutePosition />
        <SkipLink />
        <Navbar darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
        <Body as="main" id="main" tabIndex={-1}>
          <Suspense fallback={<p role="status" style={{padding:40}}>Loading page...</p>}>
          <Routes><Route path="/" element={<><Seo title={site.title} description={site.description} path="" fullTitle /><HeroSection />
          <Wrapper>
            <About />
            <Skills />
            <Projects setOpenModal={setOpenModal} />
            <Experience />
            <Education />
            <Research />
            <Contact />
          </Wrapper>
          </>} />
          <Route path="/blog" element={<BlogListing />} /><Route path="/blog/:slug" element={<BlogArticle />} />
          <Route path="*" element={<div style={{padding:40}}><h1>Page not found</h1><Link to="/blog">Go to Blog</Link></div>} />
          </Routes></Suspense>
          <Footer />
          {openModal.state && (
            <ProjectDetails openModal={openModal} setOpenModal={setOpenModal} />
          )}
        </Body>
      </MotionConfig>
      </BrowserRouter>
      </MuiThemeProvider>
    </ThemeProvider>
  );
}

export default App;
